import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Users, Phone, Mail, Pencil, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import {
    SALON_PAY_MODELS, PAY_TYPE_LABELS, allowedPayTypes, defaultStaffForm,
} from "@/lib/payroll";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const deleteBtn =
    "size-8 text-[#B8859C] hover:bg-[#FDE8EC] hover:text-[#4A0E17]";

const ROLE_STYLES = {
    stylist: { bg: "#FDF3DC", text: "#9A7B10" },
    therapist: { bg: "#E0EDFA", text: "#3B82C4" },
    manager: { bg: "#FDE8EC", text: MAROON },
    receptionist: { bg: "#FBE4EF", text: "#D9468F" },
};

const PAY_TYPE_LABELS_LOCAL = PAY_TYPE_LABELS;

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

function RoleBadge({ role }) {
    const { bg, text } = ROLE_STYLES[role] ?? { bg: "#FAF9F6", text: CHOCOLATE };
    return (
        <span
            className="inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{ background: bg, color: text }}
        >
            {role}
        </span>
    );
}

function PayTypeBadge({ payType }) {
    const styles = {
        commission: { bg: "#FDF3DC", text: "#9A7B10" },
        fixed: { bg: "#E0EDFA", text: "#3B82C4" },
        hybrid: { bg: "#FDE8EC", text: MAROON },
    };
    const { bg, text } = styles[payType] ?? styles.commission;
    return (
        <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: bg, color: text }}>
            {PAY_TYPE_LABELS_LOCAL[payType] || payType}
        </span>
    );
}

function paySummary(s, salonModel = "mixed") {
    const pt = salonModel === "fixed" ? "fixed" : salonModel === "commission" ? "commission" : (s.pay_type || "commission");
    if (pt === "fixed") {
        return `${inr(s.base_salary)} / ${s.salary_period === "weekly" ? "week" : "month"}`;
    }
    if (pt === "hybrid") {
        const base = s.base_salary ? `${inr(s.base_salary)} base` : "";
        const comm = s.commission_pct ? `${s.commission_pct}% above ${inr(s.commission_threshold || 0)}` : "";
        return [base, comm].filter(Boolean).join(" · ") || "Hybrid";
    }
    return `${s.commission_pct ?? 0}% of service revenue`;
}

export default function Staff() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editStaff, setEditStaff] = useState(null);
    const [payroll, setPayroll] = useState({ salon_pay_model: "mixed" });

    const salonModel = payroll.salon_pay_model || "mixed";

    const load = async () => {
        const [staffRes, payrollRes] = await Promise.all([
            api.get("/staff"),
            api.get("/tenant/payroll").catch(() => ({ data: { salon_pay_model: "mixed" } })),
        ]);
        setList(staffRes.data);
        setPayroll(payrollRes.data);
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Remove this staff member?")) return;
        await api.delete(`/staff/${id}`);
        toast.success("Removed");
        load();
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="staff-page">
            <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className={EYEBROW} style={{ color: GOLD }}>Team</p>
                    <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                        Staff
                    </h1>
                    <GoldDivider className="mt-3 max-w-[200px]" />
                    <p className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#8A8178]">
                        <Users className="size-4 text-[#D4AF37]" />
                        {list.length} team member{list.length !== 1 ? "s" : ""}
                        <span className="text-[#E8E4DE]">|</span>
                        <span>{SALON_PAY_MODELS[salonModel]?.short || "Pay"}</span>
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-[#4A0E17] font-semibold shadow-[0_4px_14px_rgba(74,14,23,0.25)] hover:bg-[#3a0b12]"
                            data-testid="add-staff-button"
                        >
                            <Plus className="mr-1 size-4" /> Add staff
                        </Button>
                    </DialogTrigger>
                    <StaffDialog
                        salonModel={salonModel}
                        payrollDefaults={payroll}
                        onClose={() => setOpen(false)}
                        onSaved={() => { load(); setOpen(false); }}
                    />
                </Dialog>
                <Dialog open={!!editStaff} onOpenChange={(o) => !o && setEditStaff(null)}>
                    {editStaff && (
                        <StaffDialog
                            staff={editStaff}
                            salonModel={salonModel}
                            payrollDefaults={payroll}
                            onClose={() => setEditStaff(null)}
                            onSaved={() => { load(); setEditStaff(null); }}
                        />
                    )}
                </Dialog>
            </header>

            {salonModel !== "mixed" && (
                <div className="rounded-xl border border-[#E8E4DE] bg-[#FFFEF9] px-4 py-3 text-sm font-medium text-[#5C534C]">
                    <span className="font-bold" style={{ color: CHOCOLATE }}>{SALON_PAY_MODELS[salonModel]?.label}</span>
                    {" — "}
                    {SALON_PAY_MODELS[salonModel]?.description}
                    <span className="text-[#8A8178]"> Change in Settings → How your team gets paid.</span>
                </div>
            )}

            {list.length === 0 ? (
                <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white px-6 py-14 text-center shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                    <p className="font-serif text-lg font-bold" style={{ color: CHOCOLATE }}>No staff yet</p>
                    <p className="mt-2 text-sm font-medium text-[#8A8178]">Add your first stylist or therapist.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {list.map((s) => (
                        <div
                            key={s.id}
                            className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.04)] transition-colors hover:bg-[#FAF9F6]/50"
                            data-testid={`staff-card-${s.id}`}
                        >
                            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                            <div className="flex items-start justify-between gap-2">
                                <Link
                                    to={`/app/staff/${s.id}`}
                                    className="flex min-w-0 flex-1 items-center gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/40"
                                >
                                    <span
                                        className="flex size-12 shrink-0 items-center justify-center rounded-full font-serif text-xl font-bold text-white shadow-sm ring-2 ring-white"
                                        style={{ background: s.color }}
                                    >
                                        {s.full_name.charAt(0)}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="truncate font-semibold" style={{ color: CHOCOLATE }}>{s.full_name}</div>
                                        <div className="mt-1.5 flex flex-wrap gap-1">
                                            <RoleBadge role={s.role} />
                                            {salonModel === "mixed" && (
                                                <PayTypeBadge payType={s.pay_type || "commission"} />
                                            )}
                                        </div>
                                    </div>
                                </Link>
                                <div className="flex shrink-0 gap-0.5">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className={deleteBtn}
                                        onClick={() => setEditStaff(s)}
                                        data-testid={`edit-staff-${s.id}`}
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className={deleteBtn}
                                        onClick={() => remove(s.id)}
                                        data-testid={`delete-staff-${s.id}`}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-4 space-y-2 border-t border-[#E8E4DE]/80 pt-4 text-sm font-medium text-[#8A8178]">
                                {s.mobile && (
                                    <div className="flex items-center gap-2">
                                        <Phone className="size-3.5 shrink-0 text-[#D4AF37]" />
                                        <span>{s.mobile}</span>
                                    </div>
                                )}
                                {s.email && (
                                    <div className="flex items-center gap-2 truncate">
                                        <Mail className="size-3.5 shrink-0 text-[#D4AF37]" />
                                        <span className="truncate">{s.email}</span>
                                    </div>
                                )}
                                <div className="flex items-start gap-2">
                                    <IndianRupee className="mt-0.5 size-3.5 shrink-0 text-[#D4AF37]" />
                                    <span className="font-semibold leading-snug" style={{ color: CHOCOLATE }}>{paySummary(s, salonModel)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function StaffDialog({ staff, salonModel = "mixed", payrollDefaults = {}, onClose, onSaved }) {
    const isEdit = !!staff;
    const payTypes = allowedPayTypes(salonModel);
    const [f, setF] = useState(() => {
        if (staff) {
            return {
                full_name: staff.full_name || "",
                role: staff.role || "stylist",
                mobile: staff.mobile || "",
                email: staff.email || "",
                color: staff.color || "#C84B31",
                pay_type: payTypes.includes(staff.pay_type) ? staff.pay_type : payTypes[0],
                commission_pct: staff.commission_pct ?? payrollDefaults.default_commission_pct ?? 40,
                base_salary: staff.base_salary ?? payrollDefaults.default_base_salary ?? 0,
                salary_period: staff.salary_period || "monthly",
                commission_threshold: staff.commission_threshold ?? 0,
            };
        }
        const base = defaultStaffForm(salonModel);
        return {
            ...base,
            commission_pct: payrollDefaults.default_commission_pct ?? base.commission_pct,
            base_salary: payrollDefaults.default_base_salary ?? base.base_salary,
        };
    });
    const [busy, setBusy] = useState(false);

    const setRole = (role) => setF((prev) => ({ ...prev, role }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const pay_type = salonModel === "mixed" ? f.pay_type : payTypes[0];
            const payload = {
                ...f,
                pay_type,
                commission_pct: Number(f.commission_pct) || 0,
                base_salary: Number(f.base_salary) || 0,
                commission_threshold: Number(f.commission_threshold) || 0,
            };
            if (salonModel === "fixed") {
                payload.commission_pct = 0;
                payload.commission_threshold = 0;
            }
            if (salonModel === "commission") {
                payload.base_salary = 0;
                payload.commission_threshold = 0;
                payload.pay_type = "commission";
            }
            if (!payload.email) delete payload.email;
            if (isEdit) {
                await api.put(`/staff/${staff.id}`, payload);
                toast.success("Updated");
            } else {
                await api.post("/staff", payload);
                toast.success("Added");
            }
            onSaved();
        } catch {
            toast.error("Failed");
        } finally {
            setBusy(false);
        }
    };

    const showPayTypePicker = salonModel === "mixed" && payTypes.length > 1;
    const showCommission = f.pay_type === "commission" || f.pay_type === "hybrid" || salonModel === "commission";
    const showBase = f.pay_type === "fixed" || f.pay_type === "hybrid" || salonModel === "fixed";
    const showThreshold = salonModel === "mixed" && f.pay_type === "hybrid";

    return (
        <DialogContent className="scrollbar-dialog max-h-[min(90vh,640px)] overflow-y-auto overscroll-contain rounded-xl border-[#E8E4DE] p-6 pr-5 sm:max-w-md">
            <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-bold" style={{ color: MAROON }}>
                    {isEdit ? "Edit staff" : "Add staff"}
                </DialogTitle>
                <GoldDivider className="mt-2 max-w-[140px]" />
                <p className="pt-1 text-xs font-medium text-[#8A8178]">
                    {salonModel === "fixed"
                        ? "Enter salary only — your salon uses fixed pay."
                        : salonModel === "commission"
                            ? "Enter commission % — your salon uses commission pay."
                            : "Choose how this person is paid."}
                </p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Full name</Label>
                    <Input className={fieldClass} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required data-testid="staff-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Role</Label>
                        <Select value={f.role} onValueChange={setRole}>
                            <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="stylist">Stylist</SelectItem>
                                <SelectItem value="therapist">Therapist</SelectItem>
                                <SelectItem value="manager">Manager</SelectItem>
                                <SelectItem value="receptionist">Receptionist</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {showPayTypePicker && (
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Pay type</Label>
                            <Select value={f.pay_type} onValueChange={(v) => setF({ ...f, pay_type: v })}>
                                <SelectTrigger className={fieldClass} data-testid="staff-pay-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="commission">Commission only</SelectItem>
                                    <SelectItem value="fixed">Fixed salary</SelectItem>
                                    <SelectItem value="hybrid">Base + commission</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                <div className="space-y-3 rounded-xl border border-[#E8E4DE] bg-[#FAF9F6]/60 p-4">
                    <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#9A7B10]">Pay details</p>
                    {showBase && (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Base salary (₹)</Label>
                                <Input className={fieldClass} type="number" min={0} value={f.base_salary} onChange={(e) => setF({ ...f, base_salary: e.target.value })} data-testid="staff-base-salary" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Period</Label>
                                <Select value={f.salary_period} onValueChange={(v) => setF({ ...f, salary_period: v })}>
                                    <SelectTrigger className={fieldClass}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    {showCommission && (
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Commission %</Label>
                            <Input className={fieldClass} type="number" min={0} max={100} value={f.commission_pct} onChange={(e) => setF({ ...f, commission_pct: e.target.value })} data-testid="staff-commission" />
                        </div>
                    )}
                    {showThreshold && (
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Commission after revenue (₹)</Label>
                            <Input className={fieldClass} type="number" min={0} value={f.commission_threshold} onChange={(e) => setF({ ...f, commission_threshold: e.target.value })} placeholder="e.g. 40000" />
                            <p className="text-[11px] font-medium text-[#8A8178]">Commission applies only on revenue above this amount in the report period.</p>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Mobile</Label>
                        <Input className={fieldClass} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Email</Label>
                        <Input className={fieldClass} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Color tag</Label>
                    <Input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-10 cursor-pointer border-[#E8E4DE] bg-[#FFFEF9] p-1" />
                </div>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button type="submit" className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]" disabled={busy} data-testid="save-staff-button">
                        {busy ? "Saving…" : isEdit ? "Save changes" : "Save"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
